import { useState, useRef, useEffect } from 'react';

export interface DateRange {
  preset: string;
  startDate: string;
  endDate: string;
  label: string;
}

interface DateRangePickerProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  disabled?: boolean;
}

const PRESETS: { value: string; label: string; getRange: () => { startDate: string; endDate: string } }[] = [
  {
    value: 'last_28',
    label: 'Last 28 days',
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 28);
      return { startDate: fmt(start), endDate: fmt(end) };
    },
  },
  {
    value: 'last_30',
    label: 'Last 30 days',
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      return { startDate: fmt(start), endDate: fmt(end) };
    },
  },
  {
    value: 'last_90',
    label: 'Last 90 days',
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 90);
      return { startDate: fmt(start), endDate: fmt(end) };
    },
  },
  {
    value: 'mom',
    label: 'Month over month',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date();
      return { startDate: fmt(start), endDate: fmt(end) };
    },
  },
  {
    value: 'qoq',
    label: 'Quarter over quarter',
    getRange: () => {
      const now = new Date();
      const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      const end = new Date();
      return { startDate: fmt(qStart), endDate: fmt(end) };
    },
  },
  {
    value: 'yoy',
    label: 'Year over year',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1);
      const end = new Date();
      return { startDate: fmt(start), endDate: fmt(end) };
    },
  },
  {
    value: 'custom',
    label: 'Custom range',
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      return { startDate: fmt(start), endDate: fmt(end) };
    },
  },
];

function fmt(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function computeComparison(preset: string, startDate: string, endDate: string): { prevStart: string; prevEnd: string } {
  const s = new Date(startDate);
  const e = new Date(endDate);
  const days = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));

  if (preset === 'mom') {
    const prevMonthEnd = new Date(s);
    prevMonthEnd.setDate(prevMonthEnd.getDate() - 1);
    const prevMonthStart = new Date(prevMonthEnd.getFullYear(), prevMonthEnd.getMonth(), 1);
    return { prevStart: fmt(prevMonthStart), prevEnd: fmt(prevMonthEnd) };
  }
  if (preset === 'qoq') {
    const prevQEnd = new Date(s);
    prevQEnd.setDate(prevQEnd.getDate() - 1);
    const prevQStart = new Date(prevQEnd.getFullYear(), Math.floor(prevQEnd.getMonth() / 3) * 3, 1);
    return { prevStart: fmt(prevQStart), prevEnd: fmt(prevQEnd) };
  }
  if (preset === 'yoy') {
    const prevStart = new Date(s);
    prevStart.setFullYear(prevStart.getFullYear() - 1);
    const prevEnd = new Date(e);
    prevEnd.setFullYear(prevEnd.getFullYear() - 1);
    return { prevStart: fmt(prevStart), prevEnd: fmt(prevEnd) };
  }
  const prevEnd = new Date(s);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - days);
  return { prevStart: fmt(prevStart), prevEnd: fmt(prevEnd) };
}

export { computeComparison };

export const DEFAULT_DATE_RANGE: DateRange = (() => {
  const p = PRESETS[1];
  const r = p.getRange();
  return { preset: p.value, startDate: r.startDate, endDate: r.endDate, label: p.label };
})();

export default function DateRangePicker({ dateRange, onDateRangeChange, disabled }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handlePreset = (value: string) => {
    const preset = PRESETS.find((p) => p.value === value);
    if (!preset) return;
    const r = preset.getRange();
    onDateRangeChange({ preset: value, startDate: r.startDate, endDate: r.endDate, label: preset.label });
    if (value !== 'custom') setOpen(false);
  };

  const handleCustomDate = (field: 'startDate' | 'endDate', value: string) => {
    const updated = { ...dateRange, preset: 'custom', label: 'Custom range', [field]: value };
    onDateRangeChange(updated);
  };

  const shortLabel = () => {
    if (dateRange.preset === 'custom') {
      return `${dateRange.startDate} – ${dateRange.endDate}`;
    }
    return dateRange.label;
  };

  return (
    <div className="date-range-picker" ref={ref}>
      <button
        className="command-bar-select date-range-trigger"
        onClick={() => setOpen(!open)}
        disabled={disabled}
        title="Date range"
        type="button"
      >
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span>{shortLabel()}</span>
      </button>
      {open && (
        <div className="date-range-dropdown">
          <div className="date-range-presets">
            {PRESETS.map((p) => (
              <button
                key={p.value}
                className={`date-range-preset-btn${dateRange.preset === p.value ? ' active' : ''}`}
                onClick={() => handlePreset(p.value)}
                type="button"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="date-range-custom">
            <label className="date-range-label">
              Start
              <input
                type="date"
                value={dateRange.startDate}
                max={dateRange.endDate}
                onChange={(e) => handleCustomDate('startDate', e.target.value)}
                className="date-range-input"
              />
            </label>
            <label className="date-range-label">
              End
              <input
                type="date"
                value={dateRange.endDate}
                min={dateRange.startDate}
                max={fmt(new Date())}
                onChange={(e) => handleCustomDate('endDate', e.target.value)}
                className="date-range-input"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
